'use client';

import { FormProvider } from 'react-hook-form';
import { Box, Divider, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { MuiButton } from '@/components/button/MuiButton';
import { RHFSwitchbox } from '@/components/switch/RHFSwitchbox';
import { SearchFilterList } from './SearchFilterList';
import {
  api_portalOfficeListing_filter,
  service_RequestFilter,
} from '@/init/swagger';
import { useLogic } from './logic';
import { RequireLoginContent } from '@/features/hospital/HospitalSearchFilters/PopoverContentContainer/RequireLoginContent';
import type { PublicSearchFilterType } from './logic';

export function PublicSearchFilterBox({
  close,
  selectedSearchFilters,
  searchFilters,
  searchFilterType,
}: {
  close: () => void;
  selectedSearchFilters: service_RequestFilter[];
  searchFilters: api_portalOfficeListing_filter[];
  searchFilterType: PublicSearchFilterType;
}) {
  const {
    filterFormMethod,
    handleFilter,
    handleClose,
    handleReset,
    additionStatuses,
    filters,
  } = useLogic({
    close,
    selectedSearchFilters,
    searchFilters,
    searchFilterType,
  });

  if (searchFilterType === 'requireLogin') {
    return <RequireLoginContent />;
  }

  return (
    <FormProvider {...filterFormMethod}>
      <form>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '880px',
            px: 5,
            py: 3,
            gap: 3,
            '@media (max-width: 880px)': {
              width: '100%',
              px: 3,
              py: 3,
            },
          }}
        >
          {searchFilterType === 'serviceAndAdditionStatus' && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="JP/Heading/m/Bold/Tight" sx={{ flex: 1 }}>
                  サービス・加算で絞る
                </Typography>

                <MuiButton
                  variant="text"
                  sx={{ color: '#000' }}
                  onClick={handleClose}
                >
                  とじる
                  <ClearIcon />
                </MuiButton>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '20% 1fr',
                  alignItems: 'baseline',
                  rowGap: 2,
                }}
              >
                <Typography
                  variant="JP/Body/m/Medium/Tight"
                  sx={{
                    gridRow: 1,
                    gridColumn: 1,
                    '@media (max-width: 880px)': {
                      gridRow: 1,
                      gridColumn: '1 / span 2',
                    },
                  }}
                >
                  サービス
                </Typography>

                <Box
                  sx={{
                    gridRow: 1,
                    gridColumn: 2,
                    '@media (max-width: 880px)': {
                      gridRow: 2,
                      gridColumn: '1 / span 2',
                    },
                  }}
                >
                  <SearchFilterList searchFilters={filters.services} />
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} variant="middle" />

              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '20% 1fr',
                    alignItems: 'baseline',
                    rowGap: 2,
                  }}
                >
                  <Typography
                    variant="JP/Body/m/Medium/Tight"
                    sx={{
                      gridRow: 1,
                      gridColumn: 1,
                      '@media (max-width: 880px)': {
                        gridRow: 1,
                        gridColumn: '1 / span 2',
                      },
                    }}
                  >
                    加算
                  </Typography>

                  <Box
                    sx={{
                      gridRow: 1,
                      gridColumn: 2,
                      '@media (max-width: 880px)': {
                        gridRow: 2,
                        gridColumn: '1 / span 2',
                      },
                    }}
                  >
                    <RHFSwitchbox
                      control={filterFormMethod.control}
                      name={filterFormMethod.register('isNoAddition').name}
                      label="加算なし"
                      disabled={true}
                    />
                  </Box>
                </Box>

                {Object.keys(additionStatuses).map((key) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '20% 1fr',
                      alignItems: 'baseline',
                      rowGap: 2,
                    }}
                  >
                    <Typography
                      variant="JP/Body/m/Medium/Tight"
                      sx={{
                        gridRow: 1,
                        gridColumn: 1,
                        '@media (max-width: 880px)': {
                          gridRow: 1,
                          gridColumn: '1 / span 2',
                        },
                      }}
                    >
                      {key}
                    </Typography>

                    <Box
                      sx={{
                        gridRow: 1,
                        gridColumn: 2,
                        '@media (max-width: 880px)': {
                          gridRow: 2,
                          gridColumn: '1 / span 2',
                        },
                      }}
                    >
                      <SearchFilterList
                        searchFilters={additionStatuses[key]}
                        disabled={true}
                      />
                    </Box>
                  </Box>
                ))}

                <Divider sx={{ my: 3 }} variant="middle" />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '20% 1fr',
                    alignItems: 'center', // NOTE: 選択肢が増えて2行以上になったら他の項目と同じく baseline にする
                    rowGap: 2,
                  }}
                >
                  <Typography
                    variant="JP/Body/m/Medium/Tight"
                    sx={{
                      gridRow: 1,
                      gridColumn: 1,
                      '@media (max-width: 880px)': {
                        gridRow: 1,
                        gridColumn: '1 / span 2',
                      },
                    }}
                  >
                    その他
                  </Typography>
                  <Box
                    sx={{
                      gridRow: 1,
                      gridColumn: 2,
                      '@media (max-width: 880px)': {
                        gridRow: 2,
                        gridColumn: '1 / span 2',
                      },
                    }}
                  >
                    <SearchFilterList
                      searchFilters={filters.others}
                      disabled={true}
                    />
                  </Box>
                </Box>

                {/* この親のBoxの範囲をグレーアウトな表示にしたい */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-16px',
                    left: '-16px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 'calc(100% + 32px)',
                    height: 'calc(100% + 32px)',
                    backgroundColor: 'rgba(220, 220, 220, 0.4)',
                    borderRadius: '8px',
                  }}
                >
                  <Box
                    sx={{
                      width: '50%',
                      '@media (max-width: 880px)': {
                        width: '90%',
                      },
                    }}
                  >
                    <RequireLoginContent />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Divider />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            my: 2,
            gap: 1,
          }}
        >
          <MuiButton color="primary" variant="text" onClick={handleReset}>
            選択をクリア
          </MuiButton>

          <MuiButton color="primary" onClick={handleFilter} type="submit">
            検索
          </MuiButton>
        </Box>
      </form>
    </FormProvider>
  );
}
